/**
 * Subscriptions + agency-stripe-card domain - built on apiClient via
 * useRequest, not useAppServices(). Shared by:
 *   - Accounts/components/Tabs/SaasTab.js (plan upgrade, payment info)
 *   - components/Subscriptionodal/index.js, .../AddCardModal/index.js
 *
 * Note: Subscriptionodal/index.js's old mutationFn called
 * `AppService.subscriptions.agency(...)` - no `agency` method ever existed
 * on hook/services.js's `subscriptions` object (only upgradeSubscription and
 * getPaymentInfoByAgencyId), so that call has always thrown a TypeError
 * before any network request. It's also UI-unreachable: every button in
 * that modal calls changeIframeSrc directly, never the handleSubscription
 * path that leads to it. useUpgradeSubscription below is wired to the one
 * real matching backend route (same one SaasTab already used) since the
 * payload shape in that dead code path matches upgrade-subscription's
 * exactly - preserves "this path never does anything" either way.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT } = ReqMethods;

export const subscriptionKeys = {
  all: ["subscriptions"],
  paymentInfo: (agencyId, customerId) => ["subscriptions", "payment-info", agencyId, customerId],
};

export function usePaymentInfoByAgencyId(agencyId, customerId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: subscriptionKeys.paymentInfo(agencyId, customerId),
    queryFn: async () => {
      const { response } = await request(
        GET,
        `/subscriptions/agency/${agencyId}/${customerId}`,
        {},
      );
      return response || null;
    },
    enabled: !!agencyId && !!customerId,
    ...options,
  });
}

export function useUpgradeSubscription() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/subscriptions/upgrade-subscription", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agency"] }),
  });
}

export function useAgencyStripe() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ isUpdate, payload }) =>
      request(isUpdate ? PUT : POST, "/agency/stripe", { payload, toaster: true }),
    // components/Subscriptionodal/AddCardModal/index.js's save - refreshes
    // settings/billing/components/Balance/index.js's reader of the same
    // "agency-stripe" resource name.
    onSuccess: ({ response }) => {
      if (response) {
        queryClient.invalidateQueries({ queryKey: ["agency-stripe"] });
      }
    },
  });
}
